import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styled, { keyframes } from 'react-emotion';
import { withWizard } from '@cimpress-technology/ff-react-components/lib/Wizard';
import { shapes } from '@cimpress/react-components';
import _ from 'lodash';
import { actions as termActions } from '../../../../../reducers/terms';
import { FFS_EMAIL_ADDRESS } from '../../../../../constants/appConstants';
import { SnackbarType, withSnackbar } from '../../../../snackbar/Snackbar';

const { Robot } = shapes;

const Container = styled('div')`
  display: flex;
  height: 500px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const dots = keyframes`
0%, 20% {
  color: rgba(0,0,0,0);
  text-shadow:
    .25em 0 0 rgba(0,0,0,0),
    .5em 0 0 rgba(0,0,0,0);   
}
40% {
  color: black;
  text-shadow:
    .25em 0 0 rgba(0,0,0,0),
    .5em 0 0 rgba(0,0,0,0);
    }
60% {
  text-shadow:
    .25em 0 0,
    .5em 0 0 rgba(0,0,0,0);
    }
80%, 100% {
  text-shadow:
    .25em 0 0,
    .5em 0 0;
    }
`;

const LoadingText = styled('h2')`
  &:after {
    content: ' .';
    animation: ${dots} 1s steps(5, end) infinite;
  }
`;

class CreatingContractStep extends PureComponent {
  static defaultProps = {};

  static propTypes = {};

  state = {};

  componentDidMount() {
    this.createTerm();
  }

  onPrev = () => {
    const { wizard } = this.props;
    wizard.setStep(wizard.previous);
  };

  onNext = () => {
    const { wizard } = this.props;
    wizard.setStep(wizard.next);
  };

  createTerm = () => {
    const { flow, createSnackbar, onChange, t } = this.props;
    const {
      initiator,
      acceptors,
      productPricingTerm,
      shippingPricingTerm,
      selectedSkus,
      shouldHaveGracePeriod,
      gracePeriodApprovalDays
    } = flow;

    const acceptorMerchantIds = acceptors.map(({ label, value }) => value);
    const createTermCalls = _.map(acceptorMerchantIds, acceptorMerchantId => {
      const acceptor = {
        type: 'merchants',
        id: acceptorMerchantId
      };

      const buyer = { ...acceptor };
      const seller = { ...initiator };

      let termCalls = [];

      if (productPricingTerm.termType) {
        termCalls.push(
          this.props.createPricingTerm({
            pricingTermInfo: productPricingTerm,
            selectedSkus,
            initiator,
            acceptor,
            buyer,
            seller,
            toPendingStatus: true,
            gracePeriodApprovalDays: shouldHaveGracePeriod ? gracePeriodApprovalDays : null
          })
        );
      }

      if (shippingPricingTerm.termType) {
        termCalls.push(
          this.props.createShippingTerm({
            shippingTermInfo: shippingPricingTerm,
            selectedSkus,
            initiator,
            acceptor,
            buyer,
            seller,
            toPendingStatus: true,
            gracePeriodApprovalDays: shouldHaveGracePeriod ? gracePeriodApprovalDays : null
          })
        );
      }

      return termCalls;
    });

    Promise.all(_.flatten(createTermCalls))
      .then(createdTerms => {
        onChange({ createSuccessful: true, createdTerms }, () => this.onNext());
      })
      .catch(e => {
        const statusCode = e.response.status;
        const parsedError = _.get(e, 'response.extensions.innerDetails.validationErrors[0]');
        let errorMsg = '';

        if (parsedError && parsedError.message === 'Not all skus have list prices set up.') {
          errorMsg = t('createContract:result.notAllSKUsHaveCatalogPricing');
        } else if (statusCode === 403) {
          errorMsg = t('createContract:result.notHaveCorrectPermissions');
        } else {
          errorMsg = t('createContract:result.somethingWentWrong');
        }

        errorMsg += ` ${t('createContract.result.contactInformation', { contactEmailAddress: FFS_EMAIL_ADDRESS })}`;
        createSnackbar(errorMsg, SnackbarType.error, 8);
        this.onPrev();
      });
  };

  render() {
    const { t } = this.props;
    return (
      <Container>
        <LoadingText>{t('createContract:result.creatingContractsMessage')}</LoadingText>
        <h5>{t('createContract:result.pleaseDoNotRefresh')}</h5>
        <Robot bsStyle="success" alternate />
      </Container>
    );
  }
}

const mapDispatchToProps = {
  createPricingTerm: termActions.createPricingTerm,
  createShippingTerm: termActions.createShippingTerm
};

export default withSnackbar(
  withWizard(
    connect(
      null,
      mapDispatchToProps
    )(withTranslation()(CreatingContractStep))
  )
);
